import classNames from "classnames";
import {
    catalogItemToArticleLocation,
    getCatalogItemKeyDetail,
} from "../../common/CatalogItem";
import { locationString } from "../../common/utils/catalog.util";
import { cdnImage } from "../../common/utils/image.util";
import Asset from "../Common/Asset";
import { Flex, type Gap } from "../Common/Flex";
import Label from "../Common/Label";
import "./ArticleThumbnail.css";
import type { CatalogItem as CatalogItemApi } from "@albertine/types/openapi/lmt";

type LocationFormat = "address" | "components";
type ThumbnailSize = "S" | "M" | "L";

type ThumbnailProps = {
    article: CatalogItemApi;
    locationFormat?: LocationFormat;
};

type ArticleThumbnailProps = {
    size?: ThumbnailSize;
    className?: string;
    onClick?: () => void;
} & ThumbnailProps;

const sizeToGap: Record<ThumbnailSize, Gap> = {
    S: "medium",
    M: "medium-plus",
    L: "large",
};

const sizeToClassName: Record<ThumbnailSize, string> = {
    S: "article-thumbnail--small",
    M: "article-thumbnail--medium",
    L: "article-thumbnail--large",
};

const locationFormatToFunction: Record<
    LocationFormat,
    (article: CatalogItemApi) => string
> = {
    address: (article) => article.address || "-",
    components: (article) => {
        return locationString({
            location: catalogItemToArticleLocation({
                addressComponents: article.addressComponents,
                geoPoint: article.locationGeoPoint,
            }).location,
        });
    },
};

function findFirstImageUrl(article: CatalogItemApi) {
    return (article.images || [{ url: undefined }])[0]?.url;
}

function SmallThumbnail(props: ThumbnailProps) {
    const { article } = props;
    const imageUrl = findFirstImageUrl(article);
    const keyDetail = getCatalogItemKeyDetail(article);
    return (
        <>
            <div className="article-thumbnail__image-container">
                {imageUrl && (
                    <Asset
                        src={cdnImage(imageUrl, {
                            height: 40,
                            width: 40,
                        })}
                        className="article-thumbnail__image"
                    />
                )}
            </div>

            <Flex.Column
                gap="xsmall"
                justifyContent="center"
                className="article-thumbnail__details"
            >
                <h5 className="single-line-ellipsis">{article.name}</h5>

                <Label.Small.Light.N50 className="single-line-ellipsis">
                    {keyDetail}
                </Label.Small.Light.N50>
            </Flex.Column>
        </>
    );
}

function MediumThumbnail(props: ThumbnailProps) {
    const { article } = props;
    const imageUrl = findFirstImageUrl(article);
    const keyDetail = getCatalogItemKeyDetail(article);
    return (
        <>
            <div className="article-thumbnail__image-container">
                {imageUrl && (
                    <Asset
                        src={cdnImage(imageUrl, {
                            height: 52,
                            width: 52,
                        })}
                        className="article-thumbnail__image"
                    />
                )}
            </div>

            <Flex.Column
                gap="small"
                justifyContent="center"
                className="article-thumbnail__details"
            >
                <h4 className="single-line-ellipsis">{article.name}</h4>

                <Label.Medium.Light.N50 className="single-line-ellipsis">
                    {keyDetail}
                </Label.Medium.Light.N50>
            </Flex.Column>
        </>
    );
}

function LargeThumbnail(props: ThumbnailProps) {
    const { article, locationFormat = "components" } = props;
    const imageUrl = findFirstImageUrl(article);
    const keyDetail = getCatalogItemKeyDetail(article);
    return (
        <>
            <div className="article-thumbnail__image-container">
                {imageUrl && (
                    <Asset
                        src={cdnImage(imageUrl, {
                            height: 64,
                            width: 64,
                        })}
                        className="article-thumbnail__image"
                    />
                )}
            </div>

            <Flex.Column
                gap="small"
                justifyContent="center"
                className="article-thumbnail__details"
            >
                <h4 className="single-line-ellipsis">{article.name}</h4>

                <Label.Medium.Light.N50 className="single-line-ellipsis">
                    {keyDetail}
                </Label.Medium.Light.N50>

                <Label.Small.SemiBold.N10 className="single-line-ellipsis">
                    {locationFormatToFunction[locationFormat](article)}
                </Label.Small.SemiBold.N10>
            </Flex.Column>
        </>
    );
}

const sizeToThumbnail: Record<
    ThumbnailSize,
    (props: ThumbnailProps) => React.JSX.Element
> = {
    S: SmallThumbnail,
    M: MediumThumbnail,
    L: LargeThumbnail,
};

function ArticleThumbnail(props: ArticleThumbnailProps) {
    const {
        article,
        size = "L",
        locationFormat = "components",
        className,
        onClick,
    } = props;
    const ThumnailComponent = sizeToThumbnail[size];
    return (
        <Flex.Row
            gap={sizeToGap[size]}
            alignItems="start"
            className={classNames("article-thumnail", sizeToClassName[size], className)}
            onClick={onClick}
        >
            <ThumnailComponent article={article} locationFormat={locationFormat} />
        </Flex.Row>
    );
}

export default ArticleThumbnail;
